import { AlertTriangle, Check, ListChecks, ShieldCheck } from "lucide-react";
import type { Product, ProductModerationStageResult } from "@sakura-tech-lab/dashboard-api";

import { CatalogueImage } from "@/components/catalog/catalogue-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useModerationRuleLabelMap } from "@/hooks/use-moderation-rule-labels";
import { SEVERITY_STYLES, type ModerationSeverityLevel } from "@/lib/moderation";
import { cn } from "@/lib/utils";
import { formatDateTime, humanizeKey } from "./product-detail-utils";

function stageLabel(stage: ProductModerationStageResult["dataStage"]): string {
  if (stage === "original") return "Original product data";
  if (stage === "ai_enhanced") return "AI-enhanced product data";
  return humanizeKey(stage);
}

export function ProductModerationCard({
  product,
}: {
  readonly product: Product;
}) {
  const moderation = product.moderation;
  const stageResults = moderation?.stageResults ?? [];
  const { labelFor } = useModerationRuleLabelMap(
    stageResults.map((stage) => stage.rulesetId),
  );
  if (!moderation) return null;

  return (
    <Card className="min-w-0">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
              <ShieldCheck aria-hidden="true" className="h-4 w-4" />
            </span>
            Moderation
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {moderation.status ? <DecisionBadge status={moderation.status} /> : null}
            {moderation.reviewStatus ? (
              <Badge variant="outline">Review: {humanizeKey(moderation.reviewStatus)}</Badge>
            ) : null}
            {moderation.unpublishApplied ? (
              <Badge
                variant="outline"
                className="border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
              >
                Unpublish applied
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
        {stageResults.map((stage) => (
          <ModerationStageDetails key={stage.dataStage} stage={stage} labelFor={labelFor} />
        ))}
      </CardContent>
    </Card>
  );
}

function ModerationStageDetails({
  stage,
  labelFor,
}: {
  readonly stage: ProductModerationStageResult;
  readonly labelFor: (slug: string) => string | undefined;
}) {
  const violations = stage.violations;
  const passedSlugs = stage.passedSlugs;
  const imageUrls = [...new Set([stage.imageUrl, ...(stage.imageUrls ?? [])].filter(Boolean))] as string[];
  const updatedAt = formatDateTime(stage.updatedAt);

  return (
    <section className="space-y-4 rounded-lg border p-4" aria-labelledby={`moderation-${stage.dataStage}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={`moderation-${stage.dataStage}`} className="text-base font-semibold text-foreground">
            {stageLabel(stage.dataStage)}
          </h3>
          {updatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Updated {updatedAt}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {stage.status ? <DecisionBadge status={stage.status} /> : null}
          {stage.reviewStatus ? <Badge variant="outline">Review: {humanizeKey(stage.reviewStatus)}</Badge> : null}
          {stage.unpublishApplied ? (
            <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
              Unpublish applied
            </Badge>
          ) : null}
        </div>
      </div>

      {imageUrls.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {imageUrls.map((url, index) => (
            <CatalogueImage
              key={url}
              image={{ url }}
              width={160}
              alt={`${stageLabel(stage.dataStage)} image ${index + 1}`}
              className="h-20 w-20 shrink-0 rounded-md border object-cover"
            />
          ))}
        </div>
      )}

      {(stage.name || stage.description) && (
        <div className="rounded-md bg-muted/30 p-3">
          {stage.name ? <p className="text-sm font-medium text-foreground">{stage.name}</p> : null}
          {stage.description ? <p className="mt-1 text-sm text-muted-foreground">{stage.description}</p> : null}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Check aria-hidden="true" className="h-3.5 w-3.5 text-emerald-600" />
          <span><span className="font-medium text-foreground">{passedSlugs.length}</span> passed</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-red-500" />
          <span><span className="font-medium text-foreground">{violations.length}</span> violated</span>
        </span>
        {passedSlugs.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="ml-auto h-7 gap-1.5 px-2 text-xs">
                <ListChecks aria-hidden="true" className="h-3.5 w-3.5" />
                View passed rules
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <ul className="max-h-72 space-y-1.5 overflow-y-auto">
                {passedSlugs.map((slug) => (
                  <li key={slug} className="flex items-center gap-2 text-sm text-foreground">
                    <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    {labelFor(slug) ?? humanizeKey(slug)}
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {violations.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[64px]">Image</TableHead>
                <TableHead className="w-[100px]">Severity</TableHead>
                <TableHead className="w-[220px]">Rule</TableHead>
                <TableHead>Evidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {violations.map((violation, index) => (
                <TableRow key={`${violation.slug}-${violation.imageIndex ?? "all"}-${index}`}>
                  <TableCell>
                    {violation.imageIndex !== undefined && imageUrls[violation.imageIndex] ? (
                      <ViolationImageDialog url={imageUrls[violation.imageIndex]} imageNumber={violation.imageIndex + 1} />
                    ) : <span className="text-sm text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><SeverityBadge severity={violation.severity} /></TableCell>
                  <TableCell className="min-w-0 break-words">
                    <p className="font-medium text-foreground">{labelFor(violation.slug) ?? humanizeKey(violation.slug)}</p>
                    {(violation.unverified || violation.unpublishApplied) && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {violation.unverified && <Badge variant="outline">Unverified</Badge>}
                        {violation.unpublishApplied && <Badge variant="outline" className="border-red-200 text-red-700">Offer unpublished</Badge>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="break-words text-sm leading-relaxed text-muted-foreground">
                    {violation.evidence || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function DecisionBadge({ status }: { readonly status: string }) {
  const normalized = status.toLowerCase();
  return (
    <Badge
      variant="outline"
      className={cn(
        normalized === "approved" &&
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
        normalized === "flagged" &&
          "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
        normalized === "error" &&
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
      )}
    >
      {humanizeKey(status)}
    </Badge>
  );
}

function ViolationImageDialog({
  url,
  imageNumber,
}: {
  readonly url: string;
  readonly imageNumber: number;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="block h-10 w-10 overflow-hidden rounded-md border bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Enlarge affected image ${imageNumber}`}
        >
          <CatalogueImage
            image={{ url }}
            width={80}
            alt={`Affected image ${imageNumber}`}
            className="h-full w-full object-cover"
          />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Affected image {imageNumber}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center overflow-hidden rounded-md bg-muted/30">
          <CatalogueImage
            image={{ url }}
            width={960}
            alt={`Affected image ${imageNumber}`}
            className="max-h-[70vh] w-full object-contain"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SeverityBadge({ severity }: { readonly severity: string }) {
  if (!severity) return <span className="text-sm text-muted-foreground">—</span>;
  const style = SEVERITY_STYLES[severity.toLowerCase() as ModerationSeverityLevel];
  return (
    <Badge variant="outline" className={cn("capitalize", style?.badgeClass)}>
      {humanizeKey(severity)}
    </Badge>
  );
}
